<script lang="ts">
  const COMPANY = {
    name:      'Bleunk Studio',
    tradeName: 'Bleunk',
    website:   'https://bleunk.com',
  };

  const SECTIONS = [
    { id: 'identity',   label: 'Brand Identity'  },
    { id: 'compliance', label: 'Compliance Goals'   },
    { id: 'terms',      label: 'Terms of Service'   },
    { id: 'dpa',        label: 'Data Privacy'       },
    { id: 'disputes',   label: 'Dispute Resolution' },
  ];

  const badge = {
    gold:  'bg-[#C9A87C]/10 border-[#C9A87C]/20 text-[#C9A87C]',
    sage:  'bg-[#7A9E90]/10 border-[#7A9E90]/20 text-[#7A9E90]',
    steel: 'bg-[#8E9BAF]/10 border-[#8E9BAF]/20 text-[#8E9BAF]',
  };
</script>

<svelte:head>
  <title>Legal & Brand Information — Bleunk Studio</title>
  <meta name="description" content="Bleunk Studio legal disclosures, terms of service, and global data privacy alignment. Transparent, plain-language terms for modern business." />
  <meta name="keywords" content="Bleunk terms of service, Bleunk legal, AI automation compliance, data privacy, terms and conditions, business automation legal, Bleunk Studio" />

  <!-- Open Graph -->
  <meta property="og:title" content="Legal & Brand Information — Bleunk Studio" />
  <meta property="og:description" content="Bleunk Studio legal disclosures, terms of service, and global data privacy alignment. Transparent, plain-language terms." />
  <meta property="og:url" content="https://bleunk.com/legal" />
  <meta property="og:image" content="https://bleunk.com/og-default.png" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Bleunk" />

  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://bleunk.com/legal" />

  {@html `<script type="application/ld+json">${JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://bleunk.com/legal#webpage",
        "url": "https://bleunk.com/legal",
        "name": "Legal & Brand Information — Bleunk Studio",
        "description": "Bleunk Studio legal disclosures, terms of service, and RA 10173 data privacy alignment.",
        "dateModified": "2026-01-15",
        "isPartOf": { "@id": "https://bleunk.com/#website" },
        "about": { "@id": "https://bleunk.com/#organization" },
        "inLanguage": "en-PH"
      },
      {
        "@type": "Service",
        "@id": "https://bleunk.com/#organization",
        "name": "Bleunk Studio",
        "alternateName": "Bleunk",
        "url": "https://bleunk.com",
        "email": "hello@bleunk.com",
        "foundingDate": "2024",
        "areaServed": ["PH", "US", "AU", "SG", "GB", "CA"],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "email": "hello@bleunk.com",
          "availableLanguage": ["English", "Filipino"]
        }
      }
    ]
  })}</script>`}
</svelte:head>

<section class="relative pt-32 pb-24 px-4">
  <div class="max-w-5xl mx-auto">

    <!-- Header -->
    <div class="mb-16">
      <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dim-8 bg-dim-3 text-dim-50 text-xs font-medium mb-4">
        Legal & Brand
      </div>
      <h1 class="font-display text-4xl sm:text-5xl font-bold mb-4">
        Clear terms.<br />
        <span class="gradient-accent-text">No surprises.</span>
      </h1>
      <p class="text-dim-50 max-w-xl leading-relaxed">
        Everything you need to know about how Bleunk operates, what we commit to, and how we handle your data.
      </p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">

      <!-- Sticky sidebar -->
      <nav class="lg:col-span-1">
        <div class="rounded-2xl border border-dim-6 bg-card p-4 sticky top-24">
          <div class="text-dim-40 text-xs font-medium uppercase tracking-wider mb-3">On this page</div>
          <ul class="space-y-0.5">
            {#each SECTIONS as section}
              <li>
                <a
                  href="#{section.id}"
                  class="block text-sm text-dim-50 hover:text-content py-1.5 px-2 rounded-lg hover:bg-dim-4 transition-all"
                >
                  {section.label}
                </a>
              </li>
            {/each}
          </ul>
        </div>
      </nav>

      <!-- Main content -->
      <div class="lg:col-span-3 space-y-6">

        <!-- Brand Identity -->
        <div id="identity" class="rounded-2xl border border-dim-6 bg-card p-8">
          <h2 class="font-display text-2xl font-bold mb-6 flex items-center gap-2">
            <span class="text-[#C9A87C]">§</span> Brand Identity
          </h2>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {#each [
              { label: 'Trade Name',               value: COMPANY.name      },
              { label: 'Market Specialization',    value: 'BGC & Metro Manila' },
              { label: 'Operational Model',        value: 'Remote-First Studio' },
              { label: 'Primary Website',          value: COMPANY.website   },
            ] as item}
              <div class="p-4 bg-dim-4 rounded-xl border border-dim-6">
                <dt class="text-dim-40 text-xs mb-1">{item.label}</dt>
                <dd class="text-content text-sm font-medium">{item.value}</dd>
              </div>
            {/each}
          </dl>
        </div>

        <!-- Compliance Goals -->
        <div id="compliance" class="rounded-2xl border border-dim-6 bg-card p-8">
          <h2 class="font-display text-2xl font-bold mb-6 flex items-center gap-2">
            <span class="text-[#C9A87C]">§</span> Compliance & Design Goals
          </h2>
          <div class="space-y-3">
            {#each [
              { law: 'Republic Act No. 10173 (Data Privacy Act)', scope: 'Personal Data Processing',   status: 'sage',  notes: 'Designed for privacy-by-default architecture and DPA alignment.' },
              { law: 'NPC Advisory No. 2024-04',                  scope: 'AI Systems & Personal Data', status: 'steel', notes: 'Engineered for audit logs and human-in-the-loop escalation capabilities.' },
              { law: 'Republic Act No. 8792 (E-Commerce Act)',    scope: 'Electronic Transactions',    status: 'sage',  notes: 'Digital service agreements structured per RA 8792 standards.' },
              { law: 'DICT National AI Strategy Roadmap 2.0',     scope: 'Ethical AI Deployment',      status: 'steel', notes: 'Guided by NAISR 2.0 principles: fairness, transparency, and security.' },
            ] as item}
              <div class="p-4 bg-dim-4 rounded-xl border border-dim-6">
                <div class="flex items-start justify-between gap-4 mb-2">
                  <div class="font-medium text-content text-sm">{item.law}</div>
                  <span class="inline-flex px-2 py-0.5 rounded-full border text-xs font-medium shrink-0 {badge[item.status as keyof typeof badge]}">
                    Aligned
                  </span>
                </div>
                <div class="text-dim-40 text-xs mb-1.5">{item.scope}</div>
                <div class="text-dim-50 text-xs">{item.notes}</div>
              </div>
            {/each}
          </div>
        </div>

        <!-- Terms of Service -->
        <div id="terms" class="rounded-2xl border border-dim-6 bg-card p-8">
          <h2 class="font-display text-2xl font-bold mb-6 flex items-center gap-2">
            <span class="text-[#C9A87C]">§</span> Terms of Service (Summary)
          </h2>
          <div class="space-y-4 text-sm text-dim-60 leading-relaxed">
            <p>
              <strong class="text-content">Service Description.</strong> Bleunk provides
              AI-augmented website development, migration, and monthly retainer services via
              bleunk.com. All deliverables are scoped and quoted before any payment is taken.
            </p>
            <p>
              <strong class="text-content">Project Engagements.</strong> Fixed-price projects
              are governed by the scope agreed in writing before kickoff. Bleunk guarantees delivery
              within the committed timeline or applies a discount. Retainer plans auto-renew monthly
              unless cancelled before the billing date.
            </p>
            <p>
              <strong class="text-content">Revisions.</strong> All project tiers include two rounds
              of revisions before final delivery. Retainer clients have continuous iteration built
              in by design. Scope changes beyond agreed deliverables are subject to a separate quote.
            </p>
            <p>
              <strong class="text-content">Refunds.</strong> Refunds for the current billing period
              are not issued except where required by Philippine consumer law. Disputes should be
              raised within 14 days of delivery.
            </p>
            <p>
              <strong class="text-content">Governing Law.</strong> These terms are governed by
              Philippine law. Disputes shall be resolved through DTI's consumer arbitration mechanisms
              or the appropriate Philippine courts as applicable.
            </p>
            <p class="text-dim-40 text-xs pt-4 border-t border-dim-5">
              Full Terms of Service available upon request · Last updated: January 2026.
            </p>
          </div>
        </div>

        <!-- Data Privacy Summary -->
        <div id="dpa" class="rounded-2xl border border-dim-6 bg-card p-8">
          <h2 class="font-display text-2xl font-bold mb-6 flex items-center gap-2">
            <span class="text-[#C9A87C]">§</span> Data Privacy (RA 10173 Summary)
          </h2>
          <div class="space-y-4 text-sm text-dim-60 leading-relaxed">
            <p>
              <strong class="text-content">Data Controller.</strong> Bleunk Studio
              is the Personal Information Controller (PIC) under RA 10173.
            </p>
            <p>
              <strong class="text-content">Data Protection Officer.</strong> Our designated DPO can
              be reached at <a href="mailto:hello@bleunk.com" class="text-[#C9A87C] hover:underline underline-offset-2">hello@bleunk.com</a>.
              The DPO is responsible for ensuring alignment with RA 10173 and coordinating with relevant authorities.
            </p>
            <p>
              <strong class="text-content">Purpose & Basis.</strong> We process personal data solely
              for service delivery, billing, and communication — each with a documented lawful basis.
              We do not sell personal data.
            </p>
            <p>
              <strong class="text-content">Your Rights.</strong> Under RA 10173 you have the right to
              be informed, access, rectify, erase, object, data portability, and to lodge a complaint
              with the NPC at <a href="https://www.privacy.gov.ph" class="text-[#C9A87C] hover:underline underline-offset-2" target="_blank" rel="noopener noreferrer">privacy.gov.ph</a>.
            </p>
            <p>
              <strong class="text-content">AI & Automated Decisions.</strong> All AI agent decisions 
              are logged and subject to human override to ensure fairness and accuracy.
            </p>
            <a href="/privacy" class="inline-flex items-center gap-1.5 text-[#C9A87C] hover:underline underline-offset-2 text-sm font-medium">
              Read Full Privacy Policy →
            </a>
          </div>
        </div>

        <!-- Dispute Resolution -->
        <div id="disputes" class="rounded-2xl border border-dim-6 bg-card p-8">
          <h2 class="font-display text-2xl font-bold mb-6 flex items-center gap-2">
            <span class="text-[#C9A87C]">§</span> Dispute Resolution
          </h2>
          <div class="space-y-4 text-sm text-dim-60 leading-relaxed">
            <p>We aim to resolve all disputes amicably. If you have a complaint:</p>
            <ol class="space-y-2 list-decimal list-inside text-dim-60">
              <li>Email <a href="mailto:hello@bleunk.com" class="text-[#C9A87C] hover:underline underline-offset-2">hello@bleunk.com</a> — we respond within 3 business days.</li>
              <li>If still unresolved, file with the DTI Consumer Protection Group or the NPC (for data-related matters).</li>
            </ol>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div class="p-4 bg-dim-4 rounded-xl border border-dim-6">
                <div class="font-medium text-content text-sm mb-1">DTI Consumer Protection Group</div>
                <div class="text-dim-40 text-xs">cpg@dti.gov.ph · Hotline: 1-DTI (1-384)</div>
              </div>
              <div class="p-4 bg-dim-4 rounded-xl border border-dim-6">
                <div class="font-medium text-content text-sm mb-1">National Privacy Commission</div>
                <div class="text-dim-40 text-xs">privacy.gov.ph · info@privacy.gov.ph</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div class="mt-12 text-center text-dim-30 text-xs">
      This page was last updated January 2026.<br />
      Bleunk Studio is committed to operating with integrity and excellence.
    </div>

  </div>
</section>
